/**
 * Handing bytes and text to the browser, and taking a file back from it.
 *
 * Every function here is guarded for a server render, because the console's own
 * tests render it without a DOM. Saving is best-effort: a browser that refuses
 * the anchor click costs the technician a download, never the configuration, so
 * nothing here throws at the caller.
 */

/** True when there is a document to hang an anchor off. */
const canSave = (): boolean => typeof document !== 'undefined';

/** Saves a blob under a filename, by the anchor-and-object-URL route. */
export const saveBlob = (blob: Blob, fileName: string): void => {
  if (!canSave()) return;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  anchor.click();
  URL.revokeObjectURL(url);
};

/** Saves bytes as a binary download. */
export const saveBytes = (bytes: Uint8Array, fileName: string): void => {
  saveBlob(new Blob([new Uint8Array(bytes)], { type: 'application/octet-stream' }), fileName);
};

/** Saves text as a download, in the given type. */
export const saveText = (text: string, fileName: string, mimeType: string): void => {
  saveBlob(new Blob([text], { type: mimeType }), fileName);
};

/**
 * A filename safe on every filesystem this could be copied to.
 *
 * A run of unusable characters collapses to one dash rather than one each, so a
 * name with a slash in it reads as `Room-101-riser` instead of `Room-101---riser`.
 */
export const safeFileName = (input: string, fallback: string, pattern: RegExp): string => {
  const cleaned = input
    .replace(pattern, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned === '' ? fallback : cleaned;
};

/** Reads a file the technician chose, as text. */
export const readTextFile = (file: File): Promise<string> => file.text();

/** The size of a file in bytes, for a caller that wants to refuse it early. */
export const sizeOf = (file: File): number => file.size;
